import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Settings, Save, MessageCircle, BookOpen, Plus, Edit, Trash2 } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { createApiUrl } from "@/lib/api";

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    schoolName: "",
    schoolAddress: "",
    contactNumber: "",
    emailAddress: "",
    emailNotifications: true,
    smsNotifications: false,
    attendanceAlerts: true,
    feePaymentReminders: true,
    twoFactorAuth: false,
    sessionTimeout: true,
    passwordRequirements: {
      minLength: true,
      includeNumbers: true,
      includeSpecialChars: true
    }
  });

  const [whatsappCredentials, setWhatsappCredentials] = useState({
    token: "",
    instance_id: ""
  });

  const [isLoadingWhatsapp, setIsLoadingWhatsapp] = useState(false);

  // Subject Management State
  const [subjects, setSubjects] = useState([]);
  const [isCreateSubjectOpen, setIsCreateSubjectOpen] = useState(false);
  const [isEditSubjectOpen, setIsEditSubjectOpen] = useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [newSubject, setNewSubject] = useState({
    name: "",
    description: ""
  });
  const [editingSubject, setEditingSubject] = useState({
    id: "",
    name: "",
    description: "",
    is_active: true
  });

  const { toast } = useToast();

  useEffect(() => {
    fetchWhatsappCredentials();
    fetchSubjects();
  }, []);

  const fetchWhatsappCredentials = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(createApiUrl('/admin/whatsapp-credentials'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWhatsappCredentials({
          token: data.token || "",
          instance_id: data.instance_id || ""
        });
      }
    } catch (error) {
      console.error('Error fetching WhatsApp credentials:', error);
    }
  };

  const handleSaveWhatsappCredentials = async () => {
    if (!whatsappCredentials.token || !whatsappCredentials.instance_id) {
      toast({
        title: "Missing Information",
        description: "Please provide both WhatsApp token and instance ID",
        variant: "destructive"
      });
      return;
    }

    setIsLoadingWhatsapp(true);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please login again",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(createApiUrl('/admin/whatsapp-credentials'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: whatsappCredentials.token,
          instance_id: whatsappCredentials.instance_id
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "WhatsApp Credentials Updated!",
          description: "WhatsApp credentials have been saved successfully"
        });
      } else {
        throw new Error(data.detail || 'Failed to update WhatsApp credentials');
      }
    } catch (error) {
      console.error('Error updating WhatsApp credentials:', error);
      toast({
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Failed to update WhatsApp credentials",
        variant: "destructive"
      });
    } finally {
      setIsLoadingWhatsapp(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(createApiUrl('/admin/subjects?skip=0&limit=100'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubjects(data.subjects || []);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleCreateSubject = async () => {
    if (!newSubject.name.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide subject name",
        variant: "destructive"
      });
      return;
    }

    setIsLoadingSubjects(true);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please login again",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(createApiUrl('/admin/subjects'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newSubject.name,
          description: newSubject.description
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Subject Created!",
          description: `${data.name} has been created successfully`
        });
        setNewSubject({ name: "", description: "" });
        setIsCreateSubjectOpen(false);
        fetchSubjects();
      } else {
        throw new Error(data.detail || 'Failed to create subject');
      }
    } catch (error) {
      console.error('Error creating subject:', error);
      toast({
        title: "Creation Failed",
        description: error instanceof Error ? error.message : "Failed to create subject",
        variant: "destructive"
      });
    } finally {
      setIsLoadingSubjects(false);
    }
  };

  const handleEditSubject = (subject) => {
    setEditingSubject({
      id: subject.id,
      name: subject.name,
      description: subject.description || "",
      is_active: subject.is_active
    });
    setIsEditSubjectOpen(true);
  };

  const handleUpdateSubject = async () => {
    if (!editingSubject.name.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide subject name",
        variant: "destructive"
      });
      return;
    }

    setIsLoadingSubjects(true);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please login again",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(createApiUrl(`/admin/subjects/${editingSubject.id}`), {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editingSubject.name,
          description: editingSubject.description,
          is_active: editingSubject.is_active
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Subject Updated!",
          description: `${data.name} has been updated successfully`
        });
        setIsEditSubjectOpen(false);
        fetchSubjects();
      } else {
        throw new Error(data.detail || 'Failed to update subject');
      }
    } catch (error) {
      console.error('Error updating subject:', error);
      toast({
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Failed to update subject",
        variant: "destructive"
      });
    } finally {
      setIsLoadingSubjects(false);
    }
  };

  const handleDeleteSubject = async (subjectId, subjectName) => {
    if (!confirm(`Are you sure you want to delete "${subjectName}"? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(subjectId);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please login again",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(createApiUrl(`/admin/subjects/${subjectId}`), {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok || response.status === 204) {
        toast({
          title: "Subject Deleted!",
          description: `${subjectName} has been deleted successfully`
        });
        fetchSubjects();
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete subject');
      }
    } catch (error) {
      console.error('Error deleting subject:', error);
      toast({
        title: "Deletion Failed",
        description: error instanceof Error ? error.message : "Failed to delete subject",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleSave = () => {
    toast({
      title: "Settings Saved!",
      description: "Your settings have been updated successfully"
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Settings className="w-6 h-6 text-red-600" />
            Settings
          </h1>
          <p className="text-gray-600">Manage application settings and configurations</p>
        </div>

        {/* WhatsApp Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              WhatsApp Integration
            </CardTitle>
            <p className="text-sm text-gray-600">Configure WhatsApp messaging credentials</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="whatsappToken">WhatsApp Token</Label>
              <Textarea
                id="whatsappToken"
                value={whatsappCredentials.token}
                onChange={(e) => setWhatsappCredentials({...whatsappCredentials, token: e.target.value})}
                placeholder="Enter WhatsApp API token"
                className="min-h-[100px]"
                disabled={isLoadingWhatsapp}
              />
            </div>
            <div>
              <Label htmlFor="instanceId">Instance ID</Label>
              <Textarea
                id="instanceId"
                value={whatsappCredentials.instance_id}
                onChange={(e) => setWhatsappCredentials({...whatsappCredentials, instance_id: e.target.value})}
                placeholder="Enter WhatsApp instance ID"
                className="min-h-[80px]"
                disabled={isLoadingWhatsapp}
              />
            </div>
            <Button 
              onClick={handleSaveWhatsappCredentials} 
              className="bg-green-600 hover:bg-green-700"
              disabled={isLoadingWhatsapp}
            >
              <Save className="w-4 h-4 mr-2" />
              {isLoadingWhatsapp ? "Saving..." : "Save WhatsApp Credentials"}
            </Button>
          </CardContent>
        </Card>

        {/* Subject Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              Subject Management
            </CardTitle>
            <p className="text-sm text-gray-600">Create and manage subjects for your organization</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">Total Subjects: {subjects.length}</p>
              <Dialog open={isCreateSubjectOpen} onOpenChange={setIsCreateSubjectOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Subject
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create New Subject</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="subjectName">Subject Name *</Label>
                      <Input
                        id="subjectName"
                        value={newSubject.name}
                        onChange={(e) => setNewSubject({...newSubject, name: e.target.value})}
                        placeholder="e.g., Mathematics"
                        disabled={isLoadingSubjects}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subjectDescription">Description</Label>
                      <Textarea
                        id="subjectDescription"
                        value={newSubject.description}
                        onChange={(e) => setNewSubject({...newSubject, description: e.target.value})}
                        placeholder="Subject description"
                        className="min-h-[80px]"
                        disabled={isLoadingSubjects}
                      />
                    </div>
                    <Button 
                      onClick={handleCreateSubject} 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={isLoadingSubjects}
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      {isLoadingSubjects ? 'Creating...' : 'Create Subject'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            {subjects.length > 0 && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subjects.map((subject) => (
                      <TableRow key={subject.id}>
                        <TableCell className="font-medium">{subject.name}</TableCell>
                        <TableCell className="text-sm text-gray-600">{subject.code}</TableCell>
                        <TableCell className="text-sm text-gray-600">{subject.description || 'No description'}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            subject.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {subject.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditSubject(subject)}
                              className="px-3"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteSubject(subject.id, subject.name)}
                              disabled={isDeleting === subject.id}
                              className="px-3"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Subject Dialog */}
        <Dialog open={isEditSubjectOpen} onOpenChange={setIsEditSubjectOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Subject</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="editSubjectName">Subject Name *</Label>
                <Input
                  id="editSubjectName"
                  value={editingSubject.name}
                  onChange={(e) => setEditingSubject({...editingSubject, name: e.target.value})}
                  placeholder="e.g., Mathematics"
                  disabled={isLoadingSubjects}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editSubjectDescription">Description</Label>
                <Textarea
                  id="editSubjectDescription"
                  value={editingSubject.description}
                  onChange={(e) => setEditingSubject({...editingSubject, description: e.target.value})}
                  placeholder="Subject description"
                  className="min-h-[80px]"
                  disabled={isLoadingSubjects}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="editSubjectActive" className="text-base font-medium">Active Status</Label>
                <Switch
                  id="editSubjectActive"
                  checked={editingSubject.is_active}
                  onCheckedChange={(checked) => setEditingSubject({...editingSubject, is_active: checked})}
                  disabled={isLoadingSubjects}
                />
              </div>
              <Button 
                onClick={handleUpdateSubject} 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={isLoadingSubjects}
              >
                <Save className="w-4 h-4 mr-2" />
                {isLoadingSubjects ? 'Updating...' : 'Update Subject'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle>General Settings</CardTitle>
            <p className="text-sm text-gray-600">Configure basic application settings</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="schoolName">School Name</Label>
                <Input
                  id="schoolName"
                  value={settings.schoolName}
                  onChange={(e) => setSettings({...settings, schoolName: e.target.value})}
                  placeholder="Enter school name"
                />
              </div>
              <div>
                <Label htmlFor="contactNumber">Contact Number</Label>
                <Input
                  id="contactNumber"
                  value={settings.contactNumber}
                  onChange={(e) => setSettings({...settings, contactNumber: e.target.value})}
                  placeholder="Enter contact number"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="schoolAddress">School Address</Label>
              <Input
                id="schoolAddress"
                value={settings.schoolAddress}
                onChange={(e) => setSettings({...settings, schoolAddress: e.target.value})}
                placeholder="Enter school address"
              />
            </div>
            <div>
              <Label htmlFor="emailAddress">Email Address</Label>
              <Input
                id="emailAddress"
                type="email"
                value={settings.emailAddress}
                onChange={(e) => setSettings({...settings, emailAddress: e.target.value})}
                placeholder="Enter email address"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Notification Settings</CardTitle>
            <p className="text-sm text-gray-600">Configure notification preferences</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="emailNotifications" className="text-base font-medium">Email Notifications</Label>
                <p className="text-sm text-gray-600">Receive notifications via email</p>
              </div>
              <Switch
                id="emailNotifications"
                checked={settings.emailNotifications}
                onCheckedChange={(checked) => setSettings({...settings, emailNotifications: checked})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="smsNotifications" className="text-base font-medium">SMS Notifications</Label>
                <p className="text-sm text-gray-600">Receive notifications via SMS</p>
              </div>
              <Switch
                id="smsNotifications"
                checked={settings.smsNotifications}
                onCheckedChange={(checked) => setSettings({...settings, smsNotifications: checked})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="attendanceAlerts" className="text-base font-medium">Attendance Alerts</Label>
                <p className="text-sm text-gray-600">Get alerts for low attendance</p>
              </div>
              <Switch
                id="attendanceAlerts"
                checked={settings.attendanceAlerts}
                onCheckedChange={(checked) => setSettings({...settings, attendanceAlerts: checked})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="feePaymentReminders" className="text-base font-medium">Fee Payment Reminders</Label>
                <p className="text-sm text-gray-600">Get reminders for pending fees</p>
              </div>
              <Switch
                id="feePaymentReminders"
                checked={settings.feePaymentReminders}
                onCheckedChange={(checked) => setSettings({...settings, feePaymentReminders: checked})}
              />
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Security Settings</CardTitle>
            <p className="text-sm text-gray-600">Configure security preferences</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="twoFactorAuth" className="text-base font-medium">Two-Factor Authentication</Label>
                <p className="text-sm text-gray-600">Add an extra layer of security</p>
              </div>
              <Switch
                id="twoFactorAuth"
                checked={settings.twoFactorAuth}
                onCheckedChange={(checked) => setSettings({...settings, twoFactorAuth: checked})}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="sessionTimeout" className="text-base font-medium">Session Timeout</Label>
                <p className="text-sm text-gray-600">Automatically logout after inactivity</p>
              </div>
              <Switch
                id="sessionTimeout"
                checked={settings.sessionTimeout}
                onCheckedChange={(checked) => setSettings({...settings, sessionTimeout: checked})}
              />
            </div>

            <div>
              <Label className="text-base font-medium">Password Requirements</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={settings.passwordRequirements.minLength} readOnly />
                  <span className="text-sm">Minimum 8 characters</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={settings.passwordRequirements.includeNumbers} readOnly />
                  <span className="text-sm">Include numbers</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={settings.passwordRequirements.includeSpecialChars} readOnly />
                  <span className="text-sm">Include special characters</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700">
            <Save className="w-4 h-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
